/**
 * Changes (docs/SPEC.md §8, §25.3): the conversation with the film. One
 * query per ad holds its changes newest first (`changesKey(id)`); the ad's
 * socket writes every `change` event into it (`ad/useAdSocket.ts`), and every
 * write's answer is folded in too, so the box renders from one list.
 *
 * Routes: `POST /v1/ads/{id}/changes` (202 → `{id, n, state}`), `GET …/changes`
 * (`{items}`), `POST …/changes/{n}/answer` (`{choice}` | `{text}`),
 * `…/confirm`, `…/cancel`. Errors: 409 `no_version`, `change_pending`,
 * `invalid_state`, 429 `rate_limited` (`detail.limit` `changes_hour` |
 * `changes_day`), 400 `invalid_options` (`copy/changes.ts::changeError`).
 */
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { ChangeView } from "../film/look";
import { type PricedAnswer, api, needsConfirm } from "./client";
import { ME_KEY } from "./me";

export type ChangeState =
  | "queued"
  | "working"
  | "building"
  | "applied"
  | "answered"
  | "waiting_confirm"
  | "needs_author"
  | "cannot"
  | "declined"
  | "failed"
  | "cancelled";

export type ChangeTier = "instant" | "rebuild" | "author";
export type ChangeScope =
  "object" | "object_everywhere" | "moment" | "span" | "scene" | "film";

/** A coded sentence (§8.6): the copy table has its words. */
export interface Said {
  code: string;
  params: Record<string, unknown>;
}

export interface ChangeQuestion {
  source: "ops" | "model" | "tasks";
  tasks?: ReviewTask[];
  intent?: string;
  assumptions?: string[];
  scene_names?: Record<string, string>;
  text: string;
  choices: { ref: string | null; name: string }[];
  selected: number | null;
  /** One per choice: the scene of another film or template it takes, framed
   *  in the browser from that film's manifest (§12, P28-T10), or null; null
   *  when the question has none. */
  scenes: (QuestionScene | null)[] | null;
  op?: string | null;
}

/** A candidate scene of a question: where its film's manifest is read. */
export interface QuestionScene {
  manifest_url: string;
  scene: string;
}

export interface ReviewTask {
  text: string;
  scenes: string[];
}

export interface RequestIssue {
  request: string;
  reason: string;
  next_step: "design" | "choose_object" | "retry";
  /** Safe validator categories; raw diagnostics are never sent here. */
  blockers?: string[];
  number?: number | null;
  numbers?: number[];
}

/** A change as the page shows it (`changes.py::change_view`). */
export interface Change {
  id: string;
  n: number;
  text: string;
  /** Asked by the person, or by code (version 1, a card edit, an Accept:
   *  P28-T04). */
  origin?: "person" | "code";
  context: Partial<ChangeContext>;
  tasks?: ReviewTask[] | null;
  progress?: { completed: RequestIssue[]; unfinished: RequestIssue[] } | null;
  state: ChangeState;
  phase: string | null;
  tier: ChangeTier | null;
  scope: ChangeScope | null;
  /** The model's own sentence, shown as written (≤ 280). */
  say: string | null;
  /** The coded sentences after `say`, the scope line last. */
  says: Said[];
  question: ChangeQuestion | null;
  decline: string | null;
  error_code: string | null;
  error_params: Record<string, unknown> | null;
  base_version_id: string | null;
  result_version_id: string | null;
  /** The author step's estimate the change asks a yes for (`waiting_confirm`,
   *  §15.1); null otherwise. */
  estimate_credits: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface Moment {
  scene: string;
  t: number;
}

export interface Area {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Span {
  scene: string;
  t0: number;
  t1: number;
}

/** §8.2 minus the words and the picture: what the person pointed at. */
export interface ChangeContext {
  moment: Moment | null;
  focus: string | null;
  object: string | null;
  area: Area | null;
  view: ChangeView | null;
  span: Span | null;
}

/** `POST /v1/ads/{id}/changes`'s body (§8.2). */
export interface ChangeBody extends ChangeContext {
  text: string;
  picture: string | null;
}

/** States in which a change still runs or waits for the person. */
export const RUNNING: readonly ChangeState[] = [
  "queued",
  "working",
  "building",
  "needs_author",
];
export const WAITING: readonly ChangeState[] = ["answered", "waiting_confirm"];

export function isOpen(change: Change | null | undefined): boolean {
  return (
    !!change &&
    (RUNNING.includes(change.state) || WAITING.includes(change.state))
  );
}

export const changesKey = (id: string) => ["ad-changes", id] as const;
const path = (id: string) => `/v1/ads/${encodeURIComponent(id)}/changes`;

/** The list with one change put in its place (newest first by `n`). */
export function upsertChange(
  items: readonly Change[] | undefined,
  change: Change,
  force = false,
): Change[] {
  const rest = (items ?? []).filter((c) => c.n !== change.n);
  const old = (items ?? []).find((c) => c.n === change.n);
  // an older step arriving late never replaces a newer one
  if (
    !force &&
    old?.updated_at &&
    change.updated_at &&
    Date.parse(old.updated_at) > Date.parse(change.updated_at)
  ) {
    return [...(items ?? [])];
  }
  return [change, ...rest].sort((a, b) => b.n - a.n);
}

/** Puts a change into the ad's list; `force` for a write's own answer,
 *  which is newer than anything the page holds. */
export function writeChange(
  queryClient: QueryClient,
  adId: string,
  change: Change,
  force = false,
): void {
  queryClient.setQueryData<{ items: Change[] }>(changesKey(adId), (data) => ({
    items: upsertChange(data?.items, change, force),
  }));
}

export function useChanges(adId: string, enabled = true) {
  return useQuery({
    queryKey: changesKey(adId),
    queryFn: () => api<{ items: Change[] }>(path(adId)),
    enabled,
  });
}

/** Ask for a change; the 202's `{id, n, state}` is held as a queued change
 *  until the socket brings the rest. At or above `PRICE_CONFIRM_CREDITS` the
 *  route answers `{estimate, kind, confirm: "required"}` instead and starts
 *  nothing: the box asks the person and sends it again with `confirm: true`
 *  (§15.1). A change holds credits: the balance chip is read again. */
export function useAskChange(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ChangeBody & { confirm?: boolean }) =>
      api<{ id: string; n: number; state: ChangeState } | PricedAnswer>(
        path(adId),
        {
          method: "POST",
          body: { ...body, review_tasks: true },
        },
      ),
    onSuccess: (made, body) => {
      // a change that waits for its estimate's confirm started nothing
      if (needsConfirm(made) || !("n" in made)) return;
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
      const now = new Date().toISOString();
      const known = queryClient
        .getQueryData<{ items: Change[] }>(changesKey(adId))
        ?.items.find((c) => c.n === made.n);
      if (known) return;
      writeChange(queryClient, adId, {
        id: made.id,
        n: made.n,
        text: body.text,
        context: { ...body, picture: undefined } as Partial<ChangeContext>,
        state: made.state,
        phase: null,
        tier: null,
        scope: null,
        say: null,
        says: [],
        question: null,
        decline: null,
        error_code: null,
        error_params: null,
        base_version_id: null,
        result_version_id: null,
        estimate_credits: null,
        created_at: now,
        updated_at: null,
      });
    },
  });
}

function step(adId: string, what: "answer" | "confirm" | "cancel") {
  return (n: number, body?: unknown) =>
    api<Change>(`${path(adId)}/${n}/${what}`, { method: "POST", body });
}

export type ChangeAnswer =
  | { choice: number }
  | { text: string }
  | { tasks: ReviewTask[]; intent?: string; assumptions?: string[] };

export function useAnswerChange(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ n, answer }: { n: number; answer: ChangeAnswer }) =>
      step(adId, "answer")(n, answer),
    onSuccess: (change) => writeChange(queryClient, adId, change, true),
  });
}

export function useConfirmChange(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (n: number) => step(adId, "confirm")(n),
    onSuccess: (change) => writeChange(queryClient, adId, change, true),
  });
}

/** Stop (§8.7): the change is `cancelled` at once on the server, and at
 *  once on the page (the server's answer then replaces it: a change that
 *  finished first comes back as it finished). */
export function useCancelChange(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (n: number) => step(adId, "cancel")(n),
    onMutate: (n: number) => {
      const held = queryClient
        .getQueryData<{ items: Change[] }>(changesKey(adId))
        ?.items.find((c) => c.n === n);
      if (held) {
        writeChange(
          queryClient,
          adId,
          {
            ...held,
            state: "cancelled",
            phase: null,
            error_code: "stopped",
            updated_at: null,
          },
          true,
        );
      }
    },
    onSuccess: (change) => writeChange(queryClient, adId, change, true),
  });
}
